import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllJobPosts, updateJobPostStatus } from '../services/api';

const AdminDashboard = () => {
  const jobSeekers = JSON.parse(localStorage.getItem('jobSeekers') || '[]');
  const employers = JSON.parse(localStorage.getItem('employers') || '[]');
  const reviews = JSON.parse(localStorage.getItem('company_reviews') || '[]');

  const [jobPosts, setJobPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    const loadJobPosts = async () => {
      const result = await getAllJobPosts();
      if (result.success) {
        setJobPosts(result.jobPosts);
      }
      setLoading(false);
    };
    loadJobPosts();
  }, []);

  const handleApproveJob = async (jobPostId) => {
    const result = await updateJobPostStatus(jobPostId, 'approved');
    if (result.success) {
      setJobPosts(jobPosts.map(jp =>
        jp.id === jobPostId ? { ...jp, status: 'approved' } : jp
      ));
    }
  };

  const handleRejectJob = async (jobPostId) => {
    const result = await updateJobPostStatus(jobPostId, 'rejected');
    if (result.success) {
      setJobPosts(jobPosts.map(jp =>
        jp.id === jobPostId ? { ...jp, status: 'rejected' } : jp
      ));
    }
  };

  const pendingJobs = jobPosts.filter(jp => jp.status === 'pending');
  const approvedJobs = jobPosts.filter(jp => jp.status === 'approved');
  const rejectedJobs = jobPosts.filter(jp => jp.status === 'rejected');

  const viewJobDetails = (job) => {
    setSelectedJob(job);
    setShowDetailsModal(true);
  };

  const closeModal = () => {
    setShowDetailsModal(false);
    setSelectedJob(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 py-8">
      <div className="container mx-auto px-4">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-2xl shadow-2xl p-8 mb-8 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-10 rounded-full -ml-24 -mb-24"></div>
          <div className="relative z-10">
            <div className="flex items-center mb-3">
              <span className="text-5xl mr-4">👑</span>
              <h1 className="text-4xl font-bold">
                Admin Dashboard
              </h1>
            </div>
            <p className="text-blue-100 text-lg">
              Welcome back! Manage your platform with powerful tools
            </p>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium mb-1">Job Seekers</p>
                <p className="text-4xl font-bold">{jobSeekers.length}</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <div className="text-5xl">👤</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium mb-1">Employers</p>
                <p className="text-4xl font-bold">{employers.length}</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <div className="text-5xl">💼</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm font-medium mb-1">Pending Jobs</p>
                <p className="text-4xl font-bold">{pendingJobs.length}</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <div className="text-5xl">⏳</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm font-medium mb-1">Approved Jobs</p>
                <p className="text-4xl font-bold">{approvedJobs.length}</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <div className="text-5xl">✓</div>
              </div>
            </div>
          </div>
        </div>

        {/* Job Post Management */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 mb-8 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-3 mr-4">
                <span className="text-3xl">📝</span>
              </div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Job Post Management
              </h2>
            </div>
            <Link
              to="/admin/job-management"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 font-semibold transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              View All Jobs →
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading job posts...</p>
            </div>
          ) : jobPosts.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <div className="text-4xl mb-3">📭</div>
              <p className="text-gray-500">No job posts yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Pending Jobs */}
              {pendingJobs.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-yellow-800 mb-3 flex items-center">
                    <span className="bg-yellow-100 px-3 py-1 rounded-full mr-2">⏳</span>
                    Pending Approval ({pendingJobs.length})
                  </h3>
                  <div className="space-y-3">
                    {pendingJobs.map((job) => (
                      <div
                        key={job.id}
                        className="border-2 border-yellow-200 bg-yellow-50 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="text-lg font-bold text-gray-800">{job.position}</h4>
                            <p className="text-sm text-gray-600">
                              {job.customCompanyName || job.companyName} • Posted by {job.employerName}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-sm">
                          <div className="flex items-center text-gray-600">
                            <span className="mr-1">📍</span>
                            {job.location}
                          </div>
                          <div className="flex items-center text-gray-600">
                            <span className="mr-1">💰</span>
                            {job.salary}
                          </div>
                          <div className="flex items-center text-gray-600">
                            <span className="mr-1">👥</span>
                            {job.vacancies} vacancies
                          </div>
                          <div className="flex items-center text-gray-600">
                            <span className="mr-1">📅</span>
                            {job.postedDate}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 mb-3">
                          {job.requiredSkills && job.requiredSkills.slice(0, 5).map((skill, idx) => (
                            <span key={idx} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                              {skill}
                            </span>
                          ))}
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => viewJobDetails(job)}
                            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-semibold"
                          >
                            👁️ View Details
                          </button>
                          <button
                            onClick={() => handleApproveJob(job.id)}
                            className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 font-semibold"
                          >
                            ✓ Approve
                          </button>
                          <button
                            onClick={() => handleRejectJob(job.id)}
                            className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 font-semibold"
                          >
                            ✗ Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Approved Jobs */}
              {approvedJobs.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-green-800 mb-3 flex items-center">
                    <span className="bg-green-100 px-3 py-1 rounded-full mr-2">✓</span>
                    Approved Jobs ({approvedJobs.length})
                  </h3>
                  <div className="space-y-2">
                    {approvedJobs.slice(0, 3).map((job) => (
                      <div
                        key={job.id}
                        className="border border-green-200 bg-green-50 rounded-lg p-3 flex items-center justify-between"
                      >
                        <div>
                          <p className="font-semibold text-gray-800">{job.position}</p>
                          <p className="text-sm text-gray-600">
                            {job.customCompanyName || job.companyName} • {job.location}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => viewJobDetails(job)}
                            className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-sm font-medium"
                          >
                            👁️ Details
                          </button>
                          <button
                            onClick={() => handleRejectJob(job.id)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            Revoke
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Rejected Jobs */}
              {rejectedJobs.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-red-800 mb-3 flex items-center">
                    <span className="bg-red-100 px-3 py-1 rounded-full mr-2">✗</span>
                    Rejected Jobs ({rejectedJobs.length})
                  </h3>
                  <div className="space-y-2">
                    {rejectedJobs.slice(0, 3).map((job) => (
                      <div
                        key={job.id}
                        className="border border-red-200 bg-red-50 rounded-lg p-3 flex items-center justify-between"
                      >
                        <div>
                          <p className="font-semibold text-gray-800">{job.position}</p>
                          <p className="text-sm text-gray-600">
                            {job.customCompanyName || job.companyName} • {job.location}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => viewJobDetails(job)}
                            className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-sm font-medium"
                          >
                            👁️ Details
                          </button>
                          <button
                            onClick={() => handleApproveJob(job.id)}
                            className="text-green-600 hover:text-green-800 text-sm font-medium"
                          >
                            Approve
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Platform Stats */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-3 mr-4">
                <span className="text-3xl">📊</span>
              </div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Platform Statistics
              </h2>
            </div>
            <div className="space-y-5">
              <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border-l-4 border-blue-500">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">👥</span>
                  <span className="text-gray-700 font-medium">Total Users</span>
                </div>
                <span className="text-2xl font-bold text-blue-600">
                  {jobSeekers.length + employers.length}
                </span>
              </div>
              <div className="flex items-center justify-between bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border-l-4 border-purple-500">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">⭐</span>
                  <span className="text-gray-700 font-medium">Total Reviews</span>
                </div>
                <span className="text-2xl font-bold text-purple-600">{reviews.length}</span>
              </div>
              <div className="flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border-l-4 border-green-500">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">💚</span>
                  <span className="text-gray-700 font-medium">Platform Health</span>
                </div>
                <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                  Excellent
                </span>
              </div>
            </div>
          </div>

          {/* Admin Tools */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-xl p-3 mr-4">
                <span className="text-3xl">🛠️</span>
              </div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                Admin Tools
              </h2>
            </div>
            <div className="space-y-3">
              <Link
                to="/admin/job-management"
                className="block w-full bg-gradient-to-r from-indigo-500 to-indigo-600 text-white text-center py-4 rounded-xl hover:from-indigo-600 hover:to-indigo-700 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              >
                📝 Job Post Management
              </Link>
              <Link
                to="/admin/users"
                className="block w-full bg-gradient-to-r from-green-500 to-green-600 text-white text-center py-4 rounded-xl hover:from-green-600 hover:to-green-700 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              >
                👥 Users
              </Link>
              <Link
                to="/admin/manage"
                className="block w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white text-center py-4 rounded-xl hover:from-purple-600 hover:to-purple-700 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              >
                👑 Manage Admins
              </Link>
              <Link
                to="/admin/settings"
                className="block w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white text-center py-4 rounded-xl hover:from-blue-600 hover:to-blue-700 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              >
                ⚙️ System Settings
              </Link>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl p-3 mr-4">
                <span className="text-3xl">⚡</span>
              </div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                Recent Activity
              </h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-start space-x-4 bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-xl border-l-4 border-blue-500 hover:shadow-md transition-all">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white px-3 py-1 rounded-lg text-xs font-bold shadow-md">
                  NEW
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-800 font-medium">New job seeker registered</p>
                  <p className="text-xs text-gray-500 mt-1">⏰ 2 minutes ago</p>
                </div>
              </div>
              <div className="flex items-start space-x-4 bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border-l-4 border-green-500 hover:shadow-md transition-all">
                <div className="bg-gradient-to-br from-green-500 to-green-600 text-white px-3 py-1 rounded-lg text-xs font-bold shadow-md">
                  MATCH
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-800 font-medium">AI generated 5 new matches</p>
                  <p className="text-xs text-gray-500 mt-1">⏰ 15 minutes ago</p>
                </div>
              </div>
              <div className="flex items-start space-x-4 bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border-l-4 border-purple-500 hover:shadow-md transition-all">
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white px-3 py-1 rounded-lg text-xs font-bold shadow-md">
                  REVIEW
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-800 font-medium">New company review posted</p>
                  <p className="text-xs text-gray-500 mt-1">⏰ 1 hour ago</p>
                </div>
              </div>
              <div className="flex items-start space-x-4 bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-xl border-l-4 border-yellow-500 hover:shadow-md transition-all">
                <div className="bg-gradient-to-br from-yellow-500 to-orange-500 text-white px-3 py-1 rounded-lg text-xs font-bold shadow-md">
                  UPDATE
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-800 font-medium">Employer updated job posting</p>
                  <p className="text-xs text-gray-500 mt-1">⏰ 3 hours ago</p>
                </div>
              </div>
            </div>
          </div>

          {/* System Health */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-br from-teal-500 to-green-600 rounded-xl p-3 mr-4">
                <span className="text-3xl">💚</span>
              </div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-green-600 bg-clip-text text-transparent">
                System Health
              </h2>
            </div>
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <span className="text-xl mr-2">🚀</span>
                    <span className="text-gray-700 font-medium">API Response Time</span>
                  </div>
                  <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">Good</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                  <div className="bg-gradient-to-r from-green-500 to-emerald-500 h-3 rounded-full shadow-lg transition-all" style={{ width: '85%' }}></div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <span className="text-xl mr-2">💾</span>
                    <span className="text-gray-700 font-medium">Storage Usage</span>
                  </div>
                  <span className="text-blue-600 font-bold text-sm">
                    {(JSON.stringify(localStorage).length / 1024).toFixed(2)} KB
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                  <div className="bg-gradient-to-r from-blue-500 to-cyan-500 h-3 rounded-full shadow-lg transition-all" style={{ width: '45%' }}></div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <span className="text-xl mr-2">😊</span>
                    <span className="text-gray-700 font-medium">User Satisfaction</span>
                  </div>
                  <span className="text-purple-600 font-bold text-sm">95%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full shadow-lg transition-all" style={{ width: '95%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Job Details Modal */}
        {showDetailsModal && selectedJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold">{selectedJob.position}</h2>
                    <p className="text-blue-100 mt-1">
                      {selectedJob.customCompanyName || selectedJob.companyName}
                    </p>
                  </div>
                  <button
                    onClick={closeModal}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all"
                  >
                    <span className="text-2xl">✕</span>
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    selectedJob.status === 'approved'
                      ? 'bg-green-100 text-green-800'
                      : selectedJob.status === 'rejected'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {selectedJob.status === 'approved' ? '✓ Approved'
                     : selectedJob.status === 'rejected' ? '✗ Rejected'
                     : '⏳ Pending'}
                  </span>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-6">
                {/* Basic Information */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border-l-4 border-blue-500">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    <span className="text-xl mr-2">💼</span>
                    Job Information
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Location</p>
                      <p className="font-semibold text-gray-800">📍 {selectedJob.location}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Salary</p>
                      <p className="font-semibold text-gray-800">💰 {selectedJob.salary}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Vacancies</p>
                      <p className="font-semibold text-gray-800">👥 {selectedJob.vacancies}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Type</p>
                      <p className="font-semibold text-gray-800">🕒 {selectedJob.type}</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 mb-1">Posted By</p>
                    <p className="font-semibold text-gray-800">👤 {selectedJob.employerName}</p>
                  </div>
                  <div className="mt-2">
                    <p className="text-sm text-gray-600 mb-1">Posted Date</p>
                    <p className="font-semibold text-gray-800">📅 {selectedJob.postedDate}</p>
                  </div>
                </div>

                {/* Required Skills */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-5 border-l-4 border-purple-500">
                  <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                    <span className="text-xl mr-2">🎯</span>
                    Required Skills
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedJob.requiredSkills && selectedJob.requiredSkills.map((skill, idx) => (
                      <span
                        key={idx}
                        className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border-l-4 border-green-500">
                  <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                    <span className="text-xl mr-2">📋</span>
                    Job Description
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{selectedJob.description}</p>
                </div>

                {/* Responsibilities */}
                {selectedJob.responsibilities && selectedJob.responsibilities.length > 0 && (
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-5 border-l-4 border-blue-500">
                    <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                      <span className="text-xl mr-2">✓</span>
                      Responsibilities
                    </h3>
                    <ul className="space-y-2">
                      {selectedJob.responsibilities.map((resp, idx) => (
                        <li key={idx} className="flex items-start text-gray-700">
                          <span className="text-blue-500 mr-2 mt-1">•</span>
                          <span>{resp}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Requirements */}
                {selectedJob.requirements && selectedJob.requirements.length > 0 && (
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-5 border-l-4 border-yellow-500">
                    <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                      <span className="text-xl mr-2">📌</span>
                      Requirements
                    </h3>
                    <ul className="space-y-2">
                      {selectedJob.requirements.map((req, idx) => (
                        <li key={idx} className="flex items-start text-gray-700">
                          <span className="text-yellow-600 mr-2 mt-1">•</span>
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Benefits */}
                {selectedJob.benefits && selectedJob.benefits.length > 0 && (
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-5 border-l-4 border-indigo-500">
                    <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                      <span className="text-xl mr-2">🎁</span>
                      Benefits
                    </h3>
                    <ul className="space-y-2">
                      {selectedJob.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start text-gray-700">
                          <span className="text-indigo-500 mr-2 mt-1">•</span>
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  {selectedJob.status === 'pending' && (
                    <>
                      <button
                        onClick={() => {
                          handleApproveJob(selectedJob.id);
                          closeModal();
                        }}
                        className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 font-semibold transition-all"
                      >
                        ✓ Approve Job
                      </button>
                      <button
                        onClick={() => {
                          handleRejectJob(selectedJob.id);
                          closeModal();
                        }}
                        className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 font-semibold transition-all"
                      >
                        ✗ Reject Job
                      </button>
                    </>
                  )}
                  {selectedJob.status === 'approved' && (
                    <button
                      onClick={() => {
                        handleRejectJob(selectedJob.id);
                        closeModal();
                      }}
                      className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 font-semibold transition-all"
                    >
                      Revoke Approval
                    </button>
                  )}
                  {selectedJob.status === 'rejected' && (
                    <button
                      onClick={() => {
                        handleApproveJob(selectedJob.id);
                        closeModal();
                      }}
                      className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 font-semibold transition-all"
                    >
                      Approve Job
                    </button>
                  )}
                  <button
                    onClick={closeModal}
                    className="flex-1 bg-gray-400 text-white px-6 py-3 rounded-lg hover:bg-gray-500 font-semibold transition-all"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
